import { useMemo, useState, type FormEvent } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useCreateTask } from '../../hooks/queries/useTasks';
import { useTaskTags, useCreateTaskTag, useSetTaskTags } from '../../hooks/queries/useTaskTags';
import { useActiveCollaborators } from '../../hooks/queries/useTaskCollaboration';
import { parseTaskInput, pickLeastUsedColor, suggestAssignees } from '../../lib/tasks/taskInputParser';
import {
  applyTaskTemplate,
  composerTextFromTemplate,
} from '../../lib/tasks/applyTaskTemplate';
import { withDefaultSelfAssignee } from '../../lib/tasks/taskAssignmentDefaults';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useAuthStore } from '../../store/useAuthStore';
import { PALETTE } from '../settings/TagsTab';
import { getPriorityMeta } from './taskHelpers';
import { TaskTemplatePicker } from './TaskTemplatePicker';
import type { TaskTemplate } from '../../services/tasks/taskTemplateService';

interface TaskComposerProps {
  dossierId: string | null;
  sectionId?: string | null;
  placeholder?: string;
  compact?: boolean;
  onCreated?: (taskId: string) => void;
}

export function TaskComposer({
  dossierId,
  sectionId = null,
  placeholder = 'Nouvelle tâche…',
  compact = false,
  onCreated,
}: TaskComposerProps) {
  const [content, setContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const createTask = useCreateTask();
  const { data: allTags = [] } = useTaskTags();
  const { data: collaborators = [] } = useActiveCollaborators();
  const createTag = useCreateTaskTag();
  const setTaskTags = useSetTaskTags();

  // Tags inconnus confirmés « à créer » (clés normalisées).
  const [confirmedNewTags, setConfirmedNewTags] = useState<Set<string>>(new Set());

  const fields = useEffectiveTaskFields();
  const parsed = useMemo(
    () =>
      parseTaskInput(content, {
        tags: allTags,
        collaborators,
        now: new Date(),
        allowTags: fields.tags,
        allowPriority: fields.priority,
      }),
    [content, allTags, collaborators, fields.tags, fields.priority],
  );
  const hasChips =
    selectedTemplate !== null ||
    parsed.priority !== null || parsed.dueDate !== null ||
    parsed.tagMatches.length > 0 || parsed.unknownTags.length > 0 ||
    parsed.assigneeMatches.length > 0;

  function pickTemplate(template: TaskTemplate) {
    setSelectedTemplate(template);
    setContent(
      fields.priority
        ? composerTextFromTemplate({
            content: template.content,
            description: template.description,
            priority: template.priority,
            tagIds: template.tag_ids,
            estimated_duration_minutes: template.estimated_duration_minutes,
          })
        : template.content,
    );
  }

  // Suggestions @assigné pendant la frappe : uniquement sur le mot en cours
  // (pas d'espace final), et jamais pour un @ déjà résolu en chip.
  const suggestions = useMemo(() => {
    if (/\s$/.test(content)) return [];
    const lastWord = content.split(/\s+/).pop() ?? '';
    if (parsed.assigneeMatches.some((a) => a.raw === lastWord)) return [];
    const matchedIds = new Set(parsed.assigneeMatches.map((a) => a.id));
    return suggestAssignees(lastWord, collaborators).filter((s) => !matchedIds.has(s.id));
  }, [content, collaborators, parsed.assigneeMatches]);

  /** Remplace le mot en cours par le jeton suggéré, et laisse la main. */
  function applySuggestion(insert: string) {
    setContent((prev) => {
      const tokens = prev.split(/(\s+)/);
      tokens[tokens.length - 1] = insert;
      return `${tokens.join('')} `;
    });
  }

  /** Retire la première occurrence du jeton (mot entier) du champ. */
  function removeToken(raw: string) {
    setContent((prev) =>
      prev.split(/(\s+)/).filter((part, i, arr) => {
        if (part !== raw) return true;
        // Ne retire que la première occurrence exacte.
        return arr.slice(0, i).some((p) => p === raw);
      }).join('').replace(/\s+/g, ' ').trimStart(),
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const title = parsed.title.trim();
    if (!title && !selectedTemplate) return;

    const applied = selectedTemplate
      ? applyTaskTemplate(
          {
            content: selectedTemplate.content,
            description: selectedTemplate.description,
            priority: selectedTemplate.priority,
            tagIds: selectedTemplate.tag_ids,
            estimated_duration_minutes: selectedTemplate.estimated_duration_minutes,
          },
          {
            title,
            parsedPriority: parsed.priority,
            parsedTagIds: parsed.tagMatches.map((t) => t.id),
            dueDate: parsed.dueDate,
          },
        )
      : null;

    const selfId = useAuthStore.getState().collaborator?.id ?? null;
    createTask.mutate(
      {
        content: applied?.content ?? title,
        description: applied?.description ?? undefined,
        priority: fields.priority ? (applied?.priority ?? parsed.priority ?? 3) : 3,
        due_date: parsed.dueDate,
        dossier_id: dossierId,
        section_id: fields.sections ? sectionId : null,
        estimated_duration_minutes: applied?.estimated_duration_minutes ?? undefined,
        assigneeIds: withDefaultSelfAssignee(
          parsed.assigneeMatches.map((a) => a.id),
          selfId,
        ),
      },
      {
        onSuccess: (task) => {
          setContent('');
          setConfirmedNewTags(new Set());
          setSelectedTemplate(null);
          void applySecondary(task.id, fields.tags ? applied?.tagIds : []);
          onCreated?.(task.id);
        },
        onError: (err) => {
          toast.error(`Impossible de créer la tâche : ${err.message}`);
          // eslint-disable-next-line no-console
          console.error('[TaskComposer] createTask failed', err);
        },
      },
    );
  }

  /** Tags après création. L’assignation (soi + @) est dans createTask. */
  async function applySecondary(taskId: string, templateTagIds?: string[]) {
    const warnings: string[] = [];

    if (!fields.tags) return;
    let tagIds = templateTagIds ?? parsed.tagMatches.map((t) => t.id);
    for (const u of parsed.unknownTags) {
      if (!confirmedNewTags.has(u.name.toLowerCase())) continue;
      try {
        const created = await createTag.mutateAsync({
          name: u.name,
          color: pickLeastUsedColor(PALETTE, allTags),
        });
        tagIds = [...tagIds, created.id];
      } catch {
        warnings.push(`l’étiquette « ${u.name} » n'a pas pu être créée`);
      }
    }
    if (tagIds.length > 0) {
      try {
        await setTaskTags.mutateAsync({ taskId, tagIds });
      } catch {
        warnings.push('les étiquettes n’ont pas été appliquées');
      }
    }
    if (warnings.length > 0) toast.error(`Tâche créée, mais ${warnings.join(' ; ')}.`);
  }

  const chipsRow = hasChips && (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5 ml-6">
      {selectedTemplate && (
        <Chip
          label={`Type · ${selectedTemplate.content}`}
          onRemove={() => setSelectedTemplate(null)}
        />
      )}
      {parsed.priority !== null && (
        <Chip
          className={getPriorityMeta(parsed.priority).className}
          label={getPriorityMeta(parsed.priority).label}
          onRemove={() => removeToken(parsed.priorityRaw!)}
        />
      )}
      {parsed.tagMatches.map((t) => (
        <Chip key={t.id} label={t.name} dotColor={allTags.find((x) => x.id === t.id)?.color}
          onRemove={() => removeToken(t.raw)} />
      ))}
      {parsed.unknownTags.map((u) => {
        const confirmed = confirmedNewTags.has(u.name.toLowerCase());
        return (
          <button
            key={u.raw}
            type="button"
            onClick={() =>
              setConfirmedNewTags((prev) => {
                const next = new Set(prev);
                const k = u.name.toLowerCase();
                if (next.has(k)) next.delete(k); else next.add(k);
                return next;
              })
            }
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition ${
              confirmed
                ? 'border-cime-primary bg-cime-primary/10 text-cime-primary'
                : 'border-dashed border-cime-border text-cime-text-muted hover:border-cime-primary'
            }`}
            title={confirmed ? 'Sera créée à l’envoi' : 'Cliquer pour créer cette étiquette'}
          >
            {confirmed ? '✓' : '+'} créer « {u.name} »
          </button>
        );
      })}
      {parsed.assigneeMatches.map((a) => (
        <Chip key={a.id} label={a.full_name} onRemove={() => removeToken(a.raw)} />
      ))}
      {parsed.dueDate && (
        <Chip
          label={new Date(`${parsed.dueDate}T00:00:00`).toLocaleDateString('fr-FR', {
            weekday: 'short', day: '2-digit', month: 'short',
          })}
          onRemove={() => removeToken(parsed.dueRaw!)}
        />
      )}
    </div>
  );

  // Menu d'autocomplétion @ : ancré sous le champ (au-dessus en compact,
  // le composer kanban vivant en bas de colonne). Absolute + z-20 — le
  // conteneur scrollable de la page fait la hauteur du viewport, un menu
  // de 5 entrées sous le composer du haut ne peut pas être rogné.
  const suggestionsMenu = suggestions.length > 0 && (
    <div
      className={`absolute left-6 z-20 min-w-[220px] rounded-lg border border-cime-border bg-white shadow-lg overflow-hidden ${
        compact ? 'bottom-full mb-1' : 'top-full mt-1'
      }`}
      role="listbox"
      aria-label="Suggestions d'assignés"
    >
      {suggestions.map((s) => (
        <button
          key={s.id}
          type="button"
          role="option"
          aria-selected={false}
          onMouseDown={(e) => e.preventDefault()} // garde le focus dans le champ
          onClick={() => applySuggestion(s.insert)}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-cime-text-primary hover:bg-[#fbfaf7] transition"
        >
          <Avatar name={s.full_name} src={s.avatar_url} size="xs" />
          <span className="truncate">{s.full_name}</span>
        </button>
      ))}
    </div>
  );

  if (compact) {
    return (
      <div className="relative">
        <form onSubmit={handleSubmit} className="flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5 text-cime-text-muted flex-shrink-0" />
          <input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={placeholder}
            disabled={createTask.isPending}
            data-task-composer
            className="flex-1 text-sm bg-transparent border-0 outline-none placeholder-cime-text-muted disabled:opacity-50"
          />
          {createTask.isPending && (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-cime-text-muted" />
          )}
          <TaskTemplatePicker compact onSelect={pickTemplate} />
        </form>
        {suggestionsMenu}
        {chipsRow}
      </div>
    );
  }

  return (
    <div className="relative">
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 p-4 rounded-xl border border-cime-border bg-white shadow-sm"
      >
        <Plus className="w-5 h-5 text-cime-primary flex-shrink-0" />
        <Input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          className="border-0 shadow-none focus-visible:ring-0 px-0"
          disabled={createTask.isPending}
          data-task-composer
        />
        <span
          className="w-5 h-5 rounded-full border border-cime-border text-cime-text-muted hover:border-cime-primary hover:text-cime-primary cursor-help text-[11px] font-semibold flex items-center justify-center flex-shrink-0 transition"
          title={`Syntaxe : ${[
            fields.priority && '!1-!4 priorité',
            fields.tags && '#tag',
            '@prénom',
            'échéance en fin (demain, vendredi, 15/08)',
          ].filter(Boolean).join(' · ')}`}
        >
          ?
        </span>
        <TaskTemplatePicker onSelect={pickTemplate} />
        <Button type="submit" size="sm" disabled={(!parsed.title.trim() && !selectedTemplate) || createTask.isPending}>
          {createTask.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
        </Button>
      </form>
      {suggestionsMenu}
      {chipsRow}
    </div>
  );
}

function Chip({
  label, className, dotColor, onRemove,
}: {
  label: string; className?: string; dotColor?: string; onRemove: () => void;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${
        className ?? 'border-cime-border bg-white text-cime-text-primary'
      }`}
    >
      {dotColor && (
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} aria-hidden />
      )}
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="hover:text-cime-urgent leading-none"
        aria-label={`Retirer ${label}`}
      >
        ×
      </button>
    </span>
  );
}
